export const actionTypeLabel = (type) => {
    const map = {
        101: { label: "Created", color: "#15803d", bg: "#dcfce7", icon: "✦" },
        102: { label: "Updated", color: "#b45309", bg: "#fef3c7", icon: "✎" },
        103: { label: "Deleted", color: "#b91c1c", bg: "#fee2e2", icon: "✕" },
    };
    return map[type] || { label: `Type ${type}`, color: "#6b7280", bg: "#f3f4f6", icon: "?" };
};

export const friendlyFieldName = (key) => {
    const map = {
        roleSetName: "Role Name",
        accountType: "Account Type",
        status: "Status",
        roleSetPermissions: "Permissions",
        permissionId: "Permission",
        roleId: "Role",
        accountManagerEmailId: "Account Manager Email",
        opsAccountHeadEmailId: "Ops Head Email",
        publisherAccManagerEmailTO: "Publisher Manager Email (To)",
        publisherAccManagerEmailCC: "Publisher Manager Email (CC)",
        operationsAccManagerEmailTO: "Operations Manager Email (To)",
        operationsAccManagerEmailCC: "Operations Manager Email (CC)",
        payout: "Payout Amount",
        currency: "Currency",
        paymentType: "Payment Type",
        invoiceMonth: "Invoice Month",
        invoiceYear: "Invoice Year",
        invoiceStatus: "Invoice Status",
        paymentAmount: "Payment Amount",
        paymentDate: "Payment Date",
        description: "Description",
        jsVersion: "JS Version",
        scriptGenType: "Script Gen Type",
        urlExactMatch: "URL Exact Match",
    };
    return map[key] || key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
};

export const friendlyValue = (key, val) => {
    if (val === "—" || val == null || val === "") return "—";
    if (key === "currency") return val == 1 ? "USD ($)" : val == 2 ? "INR (₹)" : val == 3 ? "HKD ($)" : val == 3 ? "EUR (€))" : val;
    if (key === "status") return val == 1 ? "Active" : val == 0 ? "Inactive" : val == 5 ? "Pending" : val;
    if (key === "accountType") return val == 1 ? "Publisher" : val == 2 ? "Internal User" : val;
    if (key === "paymentType") return val == 1 ? "Prepaid" : val == 2 ? "Postpaid" : val;
    if (key === "invoiceStatus") return val == 0 ? "Pending" : val == 1 ? "Paid" : val == 2 ? "Overdue" : val;
    if (key === "approvalStatus") return val == 0 ? "Pending" : val == 1 ? "Active" : val == 9 ? "Approved" : val == 5 ? "Pending" : val;
    if (key === "invoiceMonth") {
        const months = ["", "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"];
        return months[parseInt(val)] || val;
    }
    return String(val);
};

export const getDiff = (newChangesStr, oldChangesStr) => {
    try {
        const newObj =
            typeof newChangesStr === "string"
                ? JSON.parse(newChangesStr)
                : newChangesStr;

        const oldObj =
            typeof oldChangesStr === "string"
                ? JSON.parse(oldChangesStr)
                : oldChangesStr;

        const diffs = [];

        const SKIP_KEYS = new Set([
            "createAt",
            "createdAt",
            "updateAt",
            "updatedAt",
            "guid",
        ]);

        const compareObjects = (newObj, oldObj, path = "") => {
            const keys = new Set([
                ...Object.keys(newObj || {}),
                ...Object.keys(oldObj || {}),
            ]);

            keys.forEach((key) => {
                if (SKIP_KEYS.has(key)) return;

                const newVal = newObj?.[key];
                const oldVal = oldObj?.[key];

                const currentPath = path ? `${path}.${key}` : key;

                // Array
                if (Array.isArray(newVal) || Array.isArray(oldVal)) {
                    compareArrays(
                        currentPath,
                        Array.isArray(newVal) ? newVal : [],
                        Array.isArray(oldVal) ? oldVal : []
                    );
                    return;
                }

                // Object
                if (
                    (newVal && typeof newVal === "object") ||
                    (oldVal && typeof oldVal === "object")
                ) {
                    compareObjects(newVal || {}, oldVal || {}, currentPath);
                    return;
                }

                // Primitive
                if ((newVal ?? "") !== (oldVal ?? "")) {
                    diffs.push({
                        id: newObj?.id || oldObj?.id,
                        key: currentPath,
                        type: "changed",
                        oldVal,
                        newVal,
                    });
                }
            });
        };

        const compareArrays = (path, newArr, oldArr) => {
            // If array contains objects with id
            const hasIds =
                [...newArr, ...oldArr].length > 0 &&
                [...newArr, ...oldArr].every(
                    (x) => x && typeof x === "object" && "id" in x
                );

            if (hasIds) {
                const newMap = new Map(newArr.map((x) => [x.id, x]));
                const oldMap = new Map(oldArr.map((x) => [x.id, x]));

                // Added
                newArr.forEach((item) => {
                    if (!oldMap.has(item.id)) {
                        diffs.push({
                            id: item.id,
                            key: path,
                            type: "added",
                            newVal: item,
                        });
                    }
                });

                // Removed
                oldArr.forEach((item) => {
                    if (!newMap.has(item.id)) {
                        diffs.push({
                            id: item.id,
                            key: path,
                            type: "removed",
                            oldVal: item,
                        });
                    }
                });

                // Existing -> compare recursively
                newArr.forEach((item) => {
                    if (oldMap.has(item.id)) {
                        compareObjects(
                            item,
                            oldMap.get(item.id),
                            `${path}[${item.id}]`
                        );
                    }
                });

                return;
            }

            // Primitive arrays
            if (JSON.stringify(newArr) !== JSON.stringify(oldArr)) {
                diffs.push({
                    key: path,
                    type: "changed",
                    oldVal: oldArr,
                    newVal: newArr,
                });
            }
        };

        compareObjects(newObj, oldObj);

        return diffs;
    } catch (err) {
        console.error(err);
        return [];
    }
};

export const DiffDetail = ({ diffs }) => {
    if (!diffs.length) return null;

    return (
        <div style={{ borderTop: "1.5px dashed #e2e8f0", padding: "14px 18px", background: "#fafafa" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 10 }}>
                What Changed
            </div>

            {diffs.map((d, i) => {
                const fieldLabel = friendlyFieldName(d.key);

                if (d.type === "added") {
                    return (
                        <div key={i} style={changeRow("#dcfce7", "#bbf7d0")}>
                            <span style={colorDot("#15803d")} />
                            <div>
                                <span style={{ fontWeight: 600, color: "#15803d" }}>Permission Added — </span>
                                <span style={{ color: "#166534" }}>{d.summary}</span>
                            </div>
                        </div>
                    );
                }

                if (d.type === "removed") {
                    return (
                        <div key={i} style={changeRow("#fee2e2", "#fecaca")}>
                            <span style={colorDot("#b91c1c")} />
                            <div>
                                <span style={{ fontWeight: 600, color: "#b91c1c" }}>Permission Removed — </span>
                                <span style={{ color: "#991b1b" }}>{d.summary}</span>
                            </div>
                        </div>
                    );
                }

                // changed
                const oldDisplay = friendlyValue(d.key, d.oldVal);
                const newDisplay = friendlyValue(d.key, d.newVal);

                return (
                    <div key={i} style={changeRow("#fefce8", "#fef08a")}>
                        <span style={colorDot("#d97706")} />
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                            <span style={{ fontWeight: 600, color: "#92400e" }}>{fieldLabel}</span>
                            <span style={{ color: "#a16207", fontSize: 12 }}>changed from</span>
                            <span style={oldChip}>{oldDisplay}</span>
                            <span style={{ color: "#a16207" }}>→</span>
                            <span style={newChip}>{newDisplay}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export const changeRow = (bg, borderColor) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    background: bg,
    border: `1px solid ${borderColor}`,
    borderRadius: 8,
    padding: "9px 13px",
    marginBottom: 7,
    fontSize: 13,
    lineHeight: 1.6,
});

export const colorDot = (color) => ({
    display: "inline-block",
    width: 8,
    height: 8,
    minWidth: 8,
    borderRadius: "50%",
    background: color,
    marginTop: 5,
});

export const oldChip = {
    background: "#fee2e2",
    color: "#b91c1c",
    borderRadius: 4,
    padding: "1px 8px",
    fontFamily: "monospace",
    fontSize: 12,
    fontWeight: 700,
    textDecoration: "line-through",
};

export const newChip = {
    background: "#dcfce7",
    color: "#15803d",
    borderRadius: 4,
    padding: "1px 8px",
    fontFamily: "monospace",
    fontSize: 12,
    fontWeight: 700,
};


const TrPanelConfig = () => { }

export default TrPanelConfig;